import React, { useState, useContext } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { FaStarOfLife } from 'react-icons/fa6';
import { instance } from '../../api/axiosInstance';
import { UserContext } from '../UserContext';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { UserDTO } from '../../types/user';

interface LoginPopupProps {
  onClose: () => void;
  onSwitchToSignup: () => void;
}

export default function LoginPopup({ onClose, onSwitchToSignup }: LoginPopupProps) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [saveId, setSaveId] = useState(false);
  const { setUser } = useContext(UserContext);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await instance.post('/api/users/login', {
        username: id,
        password: password,
      });

      const data = response.data;

      // 토큰 저장
      Cookies.set('access_token', data.token, {
        expires: 7,
        secure: true,
        sameSite: 'Strict',
        path: '/',
      });

      // ID 저장 처리
      if (saveId) {
        localStorage.setItem('saveId', 'true');
        localStorage.setItem('savedId', id);
      }

      // UserDTO 타입에 맞게 사용자 정보 구성 (누락된 필드 추가)
      const userData: UserDTO = {
        id: data.id,
        username: data.username,
        fullName: data.fullName || '',
        email: data.email || '',
        phone: data.phone || '',
        referralCode: data.referralCode || '',
        profileImageUrl: data.profileImageUrl || '',
        roles: data.roles || [],
        createdAt: data.createdAt || '',
        updatedAt: data.updatedAt || '',
        lastLoginAt: data.lastLoginAt || '',
        status: data.status || 'ACTIVE',
        token: data.token,
        nickname: data.nickname || '',
        isSeller: data.isSeller || false,
        snsPublic: data.snsPublic || false,
        _seller: data._seller || null,
        uuid: data.uuid || ''
      };

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      toast.success('로그인 성공!');
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '로그인에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white/95 backdrop-blur rounded-3xl p-10 max-w-md w-full mx-4 relative shadow-2xl border border-gray-100">
        {/* 닫기 버튼 */}
        <button 
          onClick={onClose}
          className="absolute top-8 right-8 text-gray-400 hover:text-gray-600 transition-all hover:rotate-90"
        >
          ✕
        </button>

        {/* 로고 */}
        <div className="flex justify-center mb-12">
          <img
            src={`${process.env.PUBLIC_URL}/logo/logo.png`}
            alt="Creator Store"
            className="h-12 object-contain"
          />
        </div>

        <form onSubmit={handleLogin} className="space-y-7">
          {/* 아이디 입력 */}
          <div>
            <label className="text-gray-700 font-semibold mb-2 text-sm flex items-center gap-1">
              아이디
              <FaStarOfLife className="text-red-400 text-[6px]" />
            </label>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              className="w-full px-5 py-4 bg-gray-50/50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#04c2c5] focus:bg-white transition-all duration-300"
              placeholder="아이디를 입력해주세요"
              required
            />
          </div>

          {/* 비밀번호 입력 */}
          <div className="relative">
            <label className="text-gray-700 font-semibold mb-2 text-sm flex items-center gap-1">
              비밀번호
              <FaStarOfLife className="text-red-400 text-[6px]" />
            </label>
            <input
              type={passwordVisible ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-5 py-4 bg-gray-50/50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#04c2c5] focus:bg-white transition-all duration-300"
              placeholder="비밀번호를 입력해주세요"
              required
            />
            <button
              type="button"
              onClick={() => setPasswordVisible(!passwordVisible)}
              className="absolute right-4 top-[42px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              {passwordVisible ? <AiOutlineEye size={20} /> : <AiOutlineEyeInvisible size={20} />}
            </button>
          </div>

          {/* ID 저장 & 회원가입/비밀번호 찾기 */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="saveId"
                checked={saveId}
                onChange={(e) => setSaveId(e.target.checked)}
                className="w-4 h-4 text-[#04c2c5] border-gray-300 rounded-md focus:ring-[#04c2c5]"
              />
              <label htmlFor="saveId" className="ml-2 text-sm text-gray-500 select-none hover:text-gray-700 transition-colors">
                ID 저장하기
              </label>
            </div>
            <div className="space-x-4 text-sm font-medium">
              <button 
                type="button" 
                onClick={onSwitchToSignup} 
                className="text-gray-500 hover:text-[#04c2c5] transition-colors"
              >
                회원가입
              </button>
              <span className="text-gray-200">|</span>
              <button 
                type="button" 
                className="text-gray-500 hover:text-[#04c2c5] transition-colors"
              >
                아이디/비밀번호 찾기
              </button>
            </div>
          </div>

          {/* 로그인 버튼 */}
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-[#04c2c5] to-[#03b1b4] text-white py-4 rounded-xl hover:opacity-90 transition-all duration-300 font-medium text-lg shadow-lg shadow-[#04c2c5]/20 mt-8"
          >
            로그인
          </button>
        </form>
      </div>
    </div>
  );
}